import { Fade, IconButton, Paper, Typography } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'

import { PromotionSkipCount } from '../../graphql/promotions'
import { formatCount } from '../../helpers/promotionAmount'
import { skipReasonLabel } from '../../helpers/skipReasonLabels'
import { getTranslate } from '../../localization'
import SkipPromotionListMobile from './SkipPromotionListMobile'
import useStyles from './styles/index.styles'
import { isUnattributedSkip, UNATTRIBUTED_LABEL } from './unattributedSkip'

interface Props {
  rows: PromotionSkipCount[]
  /** Refetch, for the header-row refresh button §2 asks every table header to carry. */
  onRefresh: () => void
  /**
   * Computed ONCE at the screen root and passed down, rather than each table opening its
   * own useMediaQuery subscription for the same breakpoint. Matches PromotionsOverview,
   * which switches at the parent — three listeners for one boolean is three chances for the
   * screen to disagree with itself mid-resize.
   */
  isSmallScreen: boolean
}

/**
 * Skips attributed to a promotion, plus the rows that are not yours to attribute.
 *
 * THE BLANK-IDENTITY ROW IS RENDERED, EXPLICITLY LABELLED, AND NEVER HIDDEN.
 *
 * A row arrives with an empty `promotionId` and `name` when the skip belongs to a promotion
 * this operator has no standing to see — another provider's, matched by a code a guest
 * entered. The service blanks the identity rather than dropping the row, because dropping it
 * would make this list's difference from the by-reason totals the exact number withheld.
 * Filtering it out here would undo that at the last possible moment.
 *
 * The WORDING names the cause rather than the system's inability. "Not attributable" is an
 * abstraction from our side of the boundary, and an operator with no model of another
 * provider reads it as either a bug or a junk row — the two readings that make them ignore
 * the count. The footnote below carries the rest, because the operator needs the reasoning
 * more than the code does.
 */
const SkipPromotionTable = ({ rows, onRefresh, isSmallScreen }: Props) => {
  const classes = useStyles()

  // Rendered even when empty. Vanishing silently is indistinguishable from a rendering bug,
  // and this is the one state where the totals visibly fail to reconcile — reasons with
  // counts above, nothing beneath them.
  if (rows.length === 0) {
    return (
      <Paper className={classes.section} data-cy="SkipPromotionsEmpty">
        <Typography variant="h6" component="h2" className={classes.caption}>
          {getTranslate('Skips by promotion')}
        </Typography>
        <Typography variant="body1" className={classes.caveat}>
          {getTranslate('No skips could be attributed to a promotion.')}
        </Typography>
      </Paper>
    )
  }

  const hasUnattributed = rows.some((row) => row.promotionId === '')

  return (
    <Paper className={classes.section}>
      <Typography
        variant="h6"
        component="h2"
        id="skips-by-promotion-caption"
        className={classes.caption}>
        {getTranslate('Skips by promotion')}
      </Typography>
      {/* Layout only: the caption above and the note below stay single-sourced, so the
          breakpoints cannot drift on meaning — only on arrangement. */}
      {isSmallScreen ? (
        <SkipPromotionListMobile rows={rows} onRefresh={onRefresh} />
      ) : (
        <div role="table" aria-labelledby="skips-by-promotion-caption">
          <div
            role="row"
            className={`${classes.tableHead} ${classes.promotionColumns}`}>
            <Typography variant="body2" role="columnheader">
              {getTranslate('Promotion')}
            </Typography>
            <Typography variant="body2" role="columnheader">
              {getTranslate('Reason')}
            </Typography>
            <Typography
              variant="body2"
              role="columnheader"
              className={classes.numeric}>
              {getTranslate('Skips')}
            </Typography>
            {/* Not a columnheader: it names no column, and a screen reader announces the count. */}
            <IconButton
              size="small"
              role="presentation"
              className={classes.refresh}
              onClick={onRefresh}
              data-cy="SkipPromotionsRefresh"
              aria-label={getTranslate('Refresh')}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
          {rows.map((row) => {
            const unattributed = isUnattributedSkip(row)
            return (
              <Fade in timeout={600} key={`${row.promotionId}:${row.reason}`}>
                <div
                  role="row"
                  // Reason is part of the key: one promotion legitimately appears once per
                  // reason, and the unattributed bucket appears once per reason too, so
                  // promotionId alone collides and React silently renders one of them.
                  data-cy={
                    unattributed ? 'SkipUnattributedRow' : 'SkipPromotionRow'
                  }
                  className={`${classes.tableRow} ${classes.promotionColumns}`}>
                  <Typography
                    variant="body2"
                    component="span"
                    role="cell"
                    className={`${classes.label} ${
                      unattributed ? classes.unattributed : ''
                    }`}>
                    {unattributed ? getTranslate(UNATTRIBUTED_LABEL) : row.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="span"
                    role="cell"
                    data-reason={row.reason}
                    className={classes.label}>
                    {getTranslate(skipReasonLabel(row.reason))}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="span"
                    role="cell"
                    className={classes.numeric}>
                    {formatCount(row.count)}
                  </Typography>
                  {/* The header's action column, empty here — one grid constant, so every row
                  carries the slot whether or not it uses it. */}
                  <span />
                </div>
              </Fade>
            )
          })}
        </div>
      )}

      {hasUnattributed && (
        <Typography
          variant="body2"
          className={classes.caveat}
          data-cy="SkipUnattributedFootnote">
          {getTranslate(
            'Guests can enter codes for promotions belonging to other providers. Those are counted here so the totals add up, but the promotion itself is not yours to see.'
          )}
        </Typography>
      )}
    </Paper>
  )
}

export default SkipPromotionTable
